import * as util from "lib/util";
import * as path from "lib/path";

// params
const idParam = path.Param("id");
const plantIdParam = path.Param("plantId");
const holdSystemParam = path.Param("holdSystemId");
const fileNameParam = path.Param("fileName");
const businessUserIdParam = path.Param("businessUserId");
const userRoleIdParam = path.Param("userRoleId");
const noteIdParam = path.Param("noteId");
const sectorIdParam = path.Param("sectorId");


// Base paths without slashes
const containmentPath = "containments";
const sectorPath = "sectors";
const reasonPath = "reasons";
const plantPath = "plants";
const statusPath = "statuses";
const typePath = "types";
const partSerialPath = "part-serial";
const uploadPath = "uploads";
const releasePath = "release";
const unlockPath = "unlock";
const userPath = "users";
const holdPath = "holds";
const initialPath = "initial";
const subsequentPath = "subsequent";
const downloadPath = "downloads";
const searchPath = "search";
const permissionsPath = "permissions";
const templatePath = "templates";
const rolesPath = "roles";
const gmidPath = "azure/search";
const holdSystemPath = "hold-systems";
const plantUserPath = "plant-user";

// Prefixes (keeping the slash here since these are true prefixes)
const mccApiPrefix = "/mcc";
const mccSecurityPrefix = "/security";
const mccTracePrefix = "/trace";

const securityServicePaths = {
	permissions: path
		.Join(mccSecurityPrefix, userPath, permissionsPath)
		.String(),
	usersSearch: path.Join(mccSecurityPrefix, userPath, searchPath).String(),
	updateUser: path.Join(mccSecurityPrefix, userPath).String(),
	roles: path.Join(mccSecurityPrefix, rolesPath).String(),
	deleteUser: path.Join(mccSecurityPrefix, userPath, rolesPath).String(),
	searchGmid: path.Join(mccSecurityPrefix, userPath, gmidPath).String(),
	userRoleNotes: (businessUserId: string) =>
		path
			.Join(mccSecurityPrefix, userPath, businessUserIdParam, "notes")
			.Replace("businessUserId", businessUserId)
			.String(),
	updateUserRoleNote: (userRoleId: string, noteId: string) =>
		path
			.Join(
				mccSecurityPrefix,
				userPath,
				"roles",
				userRoleIdParam,
				"notes",
				noteIdParam
			)
			.Replace("userRoleId", userRoleId)
			.Replace("noteId", noteId)
			.String(),
	sectorAdminContactInfo: () =>
		path
			.Join(mccSecurityPrefix, sectorPath, "admin", "contact-info")
			.String(),
	plantUserAccessManagersContactInfo: () =>
		path
			.Join(
				mccSecurityPrefix,
				sectorPath,
				"plant-user-access-managers",
				"contact-info"
			)
			.String(),
	createPlantUser: () =>
		path
			.Join(mccSecurityPrefix, userPath, plantUserPath)
			.String(),
	getPlantUser: (businessUserId: string) =>
		path
			.Join(mccSecurityPrefix, userPath, plantUserPath, businessUserIdParam)
			.Replace("businessUserId", businessUserId)
			.String(),
	updatePlantUser: (businessUserId: string) =>
		path
			.Join(mccSecurityPrefix, userPath, plantUserPath, businessUserIdParam)
			.Replace("businessUserId", businessUserId)
			.String(),
	deleteSectorAdmin: (businessUserId: string) =>
		path
			.Join(
				mccSecurityPrefix,
				userPath,
				businessUserIdParam,
				"sector-admin"
			)
			.Replace("businessUserId", businessUserId)
			.String(),
	checkUserExists: (businessUserId: string) =>
		path
			.Join(mccSecurityPrefix, userPath, "check-user-exists", businessUserIdParam)
			.Replace("businessUserId", businessUserId)
			.String(),
	securityInternalBuildInfo: path
		.Join(mccSecurityPrefix, "internal", "build-info")
		.String(),
	addSectorAdmin: () =>
		path
			.Join(mccSecurityPrefix, userPath, "sector-admin")
			.String(),
	businessObjects: path
		.Join(mccSecurityPrefix, "business-objects")
		.String(),
	deletePlantUser: (businessUserId: string) =>
		path
			.Join(
				mccSecurityPrefix,
				userPath,
				businessUserIdParam,
				"plant-user"
			)
			.Replace("businessUserId", businessUserId)
			.String(),
};

const traceServicePaths = util.AppendPrefixToPaths(
	{
		plantTypes: path.Join(plantPath, typePath).String(),
		plantsByPlantTypeId: (id: string) =>
			path.Join(plantPath, typePath, idParam).Replace("id", id).String(),
		traceInternalBuildInfo: path.Join("internal", "build-info").String(),
	},
	mccTracePrefix
);

const mccServicePaths = util.AppendPrefixToPaths(
	{
		containmentIdPath: (id: string) =>
			path.Join(containmentPath, idParam).Replace("id", id).String(),
		addContainment: path.Join(containmentPath).String(),
		addSerials: (id: string) =>
			path
				.Join(containmentPath, idParam, uploadPath)
				.Replace("id", id)
				.String(),
		releaseSerials: (id: string) =>
			path
				.Join(containmentPath, idParam, releasePath)
				.Replace("id", id)
				.String(),
		unlockContainment: (id: string) =>
			path
				.Join(containmentPath, idParam, unlockPath)
				.Replace("id", id)
				.String(),
		containmentsSearch: path.Join(containmentPath, searchPath).String(),
		flexNetPartNumberGate: path
			.Join(containmentPath, "flexnet", "part-number-gate")
			.String(),
		plantStatusByContainment: (id: string) =>
			path
				.Join(containmentPath, idParam, plantPath, statusPath)
				.Replace("id", id)
				.String(),
		containmentTypes: path.Join(containmentPath, "types").String(),
		containmentSectors: path.Join(containmentPath, sectorPath).String(),
		sectorsByContainmentType: (typeName: string) =>
			path
				.Join(containmentPath, "types", typeName, sectorPath)
				.String(),
		containmentReasons: path.Join(containmentPath, reasonPath).String(),
		containmentStatuses: path.Join(containmentPath, statusPath).String(),
		containmentInitialHold: path
			.Join(containmentPath, holdPath, initialPath)
			.String(),
		containmentSubsequentHold: path
			.Join(containmentPath, holdPath, subsequentPath)
			.String(),
		plantsBySector: (id: string) =>
			path
				.Join(containmentPath, sectorPath, idParam, plantPath)
				.Replace("id", id)
				.String(),
		plantsBySectorType: (id: string) =>
			path
				.Join(containmentPath, "sector-types", idParam, plantPath)
				.Replace("id", id)
				.String(),
		partSerialStatuses: path.Join(partSerialPath, statusPath).String(),
		downloadExcelTemplate: (fileName: string) =>
			path
				.Join(containmentPath, templatePath, fileNameParam)
				.Replace("fileName", fileName)
				.String(),
		downloadFile: (fileName: string) =>
			path
				.Join(containmentPath, downloadPath, fileNameParam)
				.Replace("fileName", fileName)
				.String(),
		unmappedFiles: (containmentId: string) =>
			path
				.Join(containmentPath, idParam, "unmapped-files")
				.Replace("id", containmentId)
				.String(),
		triggerContainmentDataDownload: (
			containmentId: string,
			holdSystemId: string,
			plantId: string
		) =>
			path
				.Join(
					containmentPath,
					idParam,
					holdSystemPath,
					holdSystemParam,
					plantPath,
					plantIdParam,
					downloadPath
				)
				.Replace("id", containmentId)
				.Replace("plantId", plantId)
				.Replace("holdSystemId", holdSystemId)
				.String(),
		mccInternalBuildInfo: path.Join("internal", "build-info").String(),
		mccInternalEnvironment: path.Join("internal", "environment").String(),
	},
	mccApiPrefix
);

export type MccServicePaths = typeof mccServicePaths;
export type SecurityServicePaths = typeof securityServicePaths;
export type TraceServicePaths = typeof traceServicePaths;
export type Paths = MccServicePaths & SecurityServicePaths & TraceServicePaths;

export function Paths(): Paths {
	return mccServicePaths.Merge(securityServicePaths).Merge(traceServicePaths);
}

/* eslint-disable @typescript-eslint/naming-convention */
export const BUILD = "build";
export const VERSION = "version";
export const GIT_COMMIT = "git-commit";
export const BUILD_RUN = "build-run";
export const MCC_CONTAINMENT_SERVICE = "mcc-containment-service";
export const MCC_SECURITY_SERVICE = "mcc-security-service";
export const MCC_TRACE_SERVICE = "mcc-trace-service";
export const MCC_API_GATEWAY = "mcc-api-gateway";
